import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { cn } from "lib/utils";

import {
  useActivateTenantReport,
  useDeactivateTenantReport,
} from "hooks/useTenantReports";

import { TenantActionsTableProps } from "js/components/Dashboard/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Button } from "js/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "js/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";

const TenantActionsTable: React.FC<TenantActionsTableProps> = ({
  report,
  openModal,
  showText = false,
}) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Initialize hooks
  const activateReportMutation = useActivateTenantReport();
  const deactivateReportMutation = useDeactivateTenantReport();

  const goToDetail = () => {
    if (report.status === "Created" || !report.is_active) return;
    if (report.expediente_azul_folder_id) {
      openModal?.();
    } else {
      navigate(`/tenants/reports/${report.id}`);
    }
  };

  const toggleActive = () => {
    if (report.is_active) {
      setConfirmOpen(true);
    } else {
      activateReportMutation.mutate(report.id);
    }
  };

  const confirmDeactivate = () => {
    deactivateReportMutation.mutate(report.id, {
      onSuccess: () => {
        setConfirmOpen(false);
      },
    });
  };

  const isLoading =
    activateReportMutation.isPending || deactivateReportMutation.isPending;

  const disabledView = report.status === "Created" || !report.is_active;
  const disableToggle =
    report.status === "Completed" || !!report.expediente_azul_folder_id;

  const ActionButtons = () => (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={goToDetail}
        disabled={disabledView}
        className="gap-2"
      >
        <Icon icon="ion:eye-outline" fontSize={16} />
        Ver detalles
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={toggleActive}
        disabled={disableToggle || isLoading}
        className={cn(
          "gap-2",
          report.is_active
            ? "border-amber-200 text-amber-600 hover:bg-amber-50"
            : "border-emerald-200 text-emerald-600 hover:bg-emerald-50",
        )}
      >
        <Icon
          icon={
            report.is_active
              ? "ion:power-outline"
              : "ion:checkmark-circle-outline"
          }
          fontSize={16}
        />
        {report.is_active ? "Desactivar" : "Activar"}
      </Button>
    </div>
  );

  const menuSections = [
    {
      heading: "Navegar",
      items: [
        {
          key: "view",
          label: "Ver detalles",
          icon: "ion:eye-outline",
          disabled: disabledView,
          onSelect: goToDetail,
        },
      ],
    },
    {
      heading: "Estado",
      items: [
        {
          key: "toggle",
          label: report.is_active ? "Desactivar" : "Activar",
          icon: report.is_active
            ? "solar:power-bold"
            : "ion:checkmark-circle-outline",
          disabled: disableToggle,
          onSelect: toggleActive,
        },
      ],
    },
  ];

  return (
    <>
      {showText ? (
        <ActionButtons />
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-100"
            >
              <Icon icon="mdi:dots-vertical" fontSize={18} />
              <span className="sr-only">Abrir acciones</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-0">
            <Command>
              <CommandList>
                <CommandEmpty>Sin acciones disponibles</CommandEmpty>
                {menuSections.map((section, index) => (
                  <React.Fragment key={section.heading}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup heading={section.heading}>
                      {section.items.map((item) => (
                        <CommandItem
                          key={item.key}
                          value={item.key}
                          disabled={item.disabled}
                          onSelect={() => item.onSelect()}
                          className="cursor-pointer"
                        >
                          <Icon
                            icon={item.icon}
                            fontSize={16}
                            className="text-slate-400"
                          />
                          {item.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar reporte</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que quieres archivar esta investigación? Los cobros
              hechos no son reembolsables.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={confirmDeactivate}
              disabled={isLoading}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TenantActionsTable;
